import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useToast } from "@/components/ui/use-toast";
import {
  CreateGroupModalProps,
  Initial,
  locationOptions,
} from "@/features/groups/components/CreateGroupModal/CreateGroupModal";
import {
  setShowCreateGroupChildModal,
  setTreeMap,
} from "@/features/groups/redux/group.slice";
import { useAppDispatch, useAppSelector } from "@/redux/store";
import {
  getGroupDescriptionElement,
  getGroupIconElement,
  getGroupTextElement,
} from "@/utils/groupElementType";
import { getMessage } from "@/utils/message";
import {
  DataStructured,
  InformationFilled,
  Warning,
} from "@carbon/icons-react";
import { AxiosError } from "axios";
import { Field, Form, Formik } from "formik";
import React, { FC, memo, useEffect, useState } from "react";
import "react-multi-carousel/lib/styles.css";
import * as Yup from "yup";
import { createGroup, getGroupTreeMap } from "../../../api/group.api";
import {
  GroupModeEnums,
  groupModeList,
} from "../../../constants/group.constant";

const CreateChildGroup: FC<CreateGroupModalProps> = ({
  rootMode = GroupModeEnums.PUBLIC,
  rootId,
  rootGroup,
}) => {
  const dispatch = useAppDispatch();
  const { toast } = useToast();
  const { user } = useAppSelector((state) => state.auth);
  const { data: groupData, showCreateGroupChildModal } = useAppSelector(
    (state) => state.group
  );

  const CreateGroupSchema = Yup.object().shape({
    name: Yup.string()
      .max(
        50,
        `Tên ${rootId ? "nhóm" : "tổ chức"} không được vượt quá 50 ký tự`
      )
      .trim()
      .required(`Tên ${rootId ? "nhóm" : "tổ chức"} không được bỏ trống.`),
    description: Yup.string().max(
      400,
      `Mô tả ${rootId ? "nhóm" : "tổ chức"} không vượt quá 400 ký tự`
    ),
  });

  const [existingNameError, setExistingNameError] = useState<null | string>(
    null
  );
  const [openModal, setOpenModal] = useState(showCreateGroupChildModal);
  const [hiddenModal] = useState(false);

  useEffect(() => {
    setOpenModal(showCreateGroupChildModal);
  }, [showCreateGroupChildModal]);

  return (
    <>
      <Dialog
        open={openModal}
        onOpenChange={(open) => {
          if (open) {
            setExistingNameError(null);
          }
        }}
      >
        <DialogContent
          className={hiddenModal ? "hidden" : "sm:max-w-[552px] bg-white gap-6"}
        >
          <div>
            <DialogTitle>{rootId ? "Tạo nhóm" : "Tạo tổ chức"}</DialogTitle>
            <DialogDescription className="text-sm text-secondary-600">
              {rootId ? (
                <p>
                  Nhóm mới được tạo sẽ trực thuộc Tổ chức, bạn có thể tuỳ chỉnh
                  vị trí nhóm trong chức năng
                  <span>
                    <DataStructured className="ml-1 mr-1 inline font-semibold text-xs" />
                    Cơ cấu tổ chức
                  </span>
                </p>
              ) : (
                "Chúng tôi sẽ tinh chỉnh trải nghiệm thiết lập của bạn cho phù hợp"
              )}
            </DialogDescription>
          </div>
          <Formik
            initialValues={{
              name: "",
              description: "",
              mode: rootMode,
              isTagaCloud: true,
            }}
            validationSchema={CreateGroupSchema}
            onSubmit={(values: Initial, { setSubmitting }) => {
              const createGroupFn = async () => {
                try {
                  if (rootId) {
                    values.parentId = rootId;
                  }
                  values.mode = Number(values.mode);
                  await createGroup(values);
                  const result = await getGroupTreeMap(rootId + "");
                  dispatch(
                    setTreeMap(
                      result.data.sort((a, b) => a.groupId - b.groupId)
                    )
                  );

                  toast({
                    title: "Thành công!",
                    variant: "success",
                    description: getMessage(
                      "MSG3",
                      `${rootId ? "nhóm" : "tổ chức"}`
                    ),
                  });
                  dispatch(setShowCreateGroupChildModal(false));
                  setOpenModal(false);
                } catch (error: any) {
                  if (error instanceof AxiosError) {
                    if (error.response?.data.errors.includes("MSG2")) {
                      setExistingNameError(
                        "Tên nhóm đã tồn tại, vui lòng điền giá trị khác."
                      );
                    }
                  } else {
                    setExistingNameError(null);
                    toast({
                      title: "Thất bại!",
                      variant: "error",
                      description: getMessage(error.response?.data.errors[0]),
                    });
                  }
                } finally {
                  setSubmitting(false);
                }
              };
              createGroupFn();
            }}
          >
            {({
              handleChange,
              setFieldValue,
              errors,
              isSubmitting,
              isValid,
            }) => (
              <Form className="grid gap-6">
                <div className="grid gap-4">
                  <div className="items-center grid gap-2">
                    <Label className="text-sm">
                      Tên {rootId ? "nhóm" : "tổ chức"}{" "}
                      <span className="text-error-600">*</span>
                    </Label>
                    <Field
                      name="name"
                      as={Input}
                      placeholder={rootId ? "Tên nhóm" : "Tên tổ chức"}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        if (e.currentTarget.value === "") {
                          setExistingNameError(null);
                        }
                        handleChange(e);
                      }}
                      error={errors.name || existingNameError}
                    />
                    {errors.name ? (
                      <p className="text-error-400 text-xs flex">
                        <Warning
                          className="text-error-400 mr-1"
                          size={16}
                        />
                        {errors.name}
                      </p>
                    ) : null}
                    {existingNameError && (
                      <p className="text-error-400 text-xs flex">
                        <Warning
                          className="text-error-400 mr-1"
                          size={16}
                        />
                        {existingNameError}
                      </p>
                    )}
                  </div>
                  <div className="items-center grid gap-2">
                    <Label className="text-sm flex items-center">
                      Quyền truy cập{" "}
                      {rootId && (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger
                              className="ml-1"
                              asChild
                            >
                              <InformationFilled />
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>
                                Quyền truy cập của Nhóm phụ thuộc vào quyền truy
                                cập của Tổ chức.
                              </p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      )}
                    </Label>
                    <Select
                      defaultValue={String(rootMode)}
                      onValueChange={(value) => setFieldValue("mode", value)}
                      disabled={!!rootId}
                    >
                      <SelectTrigger className="bg-secondary-50 items-start">
                        <SelectValue defaultValue={String(rootMode)} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectGroup className="max-w-[520px]">
                          {groupModeList.map((mode) => (
                            <SelectItem
                              key={mode}
                              value={String(mode)}
                            >
                              <div className="flex items-start gap-3 py-2">
                                {getGroupIconElement({
                                  mode,
                                  size: 16,
                                  className: "text-gray-500 mt-0.5 shrink-0",
                                })}
                                <div className="text-left">
                                  <p className="text-sm font-medium text-gray-900">
                                    {getGroupTextElement({ mode })}
                                  </p>
                                  <p className="text-xs text-gray-600 mt-1">
                                    {getGroupDescriptionElement({
                                      mode,
                                      isOrganization: !rootId,
                                    })}
                                  </p>
                                </div>
                              </div>
                            </SelectItem>
                          ))}
                        </SelectGroup>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="items-center grid gap-2">
                    <Label className="text-sm flex items-center">
                      Nơi lưu{" "}
                      {rootId && (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger
                              className="ml-1"
                              asChild
                            >
                              <InformationFilled />
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>
                                Nơi lưu trữ của nhóm phụ thuộc vào nơi lưu trữ
                                của tổ chức
                              </p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      )}
                    </Label>
                    <Select
                      defaultValue={(() => {
                        if (groupData?.savePlaceOption) {
                          return groupData?.savePlaceOption === "taga-cloud"
                            ? "true"
                            : "false";
                        }
                        return "true";
                      })()}
                      onValueChange={(value) =>
                        setFieldValue("isTagaCloud", Boolean(value === "true"))
                      }
                      disabled={!!rootId}
                    >
                      <SelectTrigger className="bg-secondary-50 items-start">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectGroup className="max-w-[520px]">
                          {locationOptions.map((option, index) => (
                            <SelectItem
                              key={index}
                              value={String(option.value)}
                            >
                              <div className="flex items-start gap-3 py-2">
                                {option.icon}
                                <div className="text-left">
                                  <p className="text-sm font-medium text-gray-900">
                                    {option.label}
                                  </p>
                                  <p className="text-xs text-gray-600 mt-1">
                                    {option.description}
                                  </p>
                                </div>
                              </div>
                            </SelectItem>
                          ))}
                        </SelectGroup>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="items-center grid gap-2">
                    <Label className="text-sm">
                      Mô tả {rootId ? "nhóm" : "tổ chức"}
                    </Label>
                    <Field
                      name="description"
                      as={Textarea}
                      placeholder={
                        rootId
                          ? "Nhóm của chúng tôi là"
                          : "Tổ chức của chúng tôi là..."
                      }
                      onChange={handleChange}
                      error={errors.description}
                    />
                    {errors.description ? (
                      <p className="text-error-400 text-xs flex">
                        <Warning
                          className="text-error-400 mr-1"
                          size={16}
                        />
                        {errors.description}
                      </p>
                    ) : null}
                  </div>
                  {rootId && user?.id !== rootGroup?.rootOwnerId && (
                    <div className="items-top flex space-x-2">
                      <Checkbox
                        name="inviteOwner"
                        id="inviteOwner"
                        onCheckedChange={(value) => {
                          setFieldValue("inviteOwner", value);
                        }}
                      />
                      <div className="grid leading-none">
                        <label
                          htmlFor="inviteOwner"
                          className="text-sm font-medium text-secondary-900 leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                        >
                          Thêm chủ sở hữu
                        </label>
                        <p className="text-xs text-secondary-600 text-muted-foreground">
                          Bạn sẽ thêm chủ sở hữu của Tổ chức là thành viên của
                          Nhóm này.
                        </p>
                      </div>
                    </div>
                  )}
                </div>
                <DialogFooter>
                  <Button
                    variant={"ghost"}
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setOpenModal(false);
                      dispatch(setShowCreateGroupChildModal(false));
                    }}
                    loading={isSubmitting}
                  >
                    Hủy
                  </Button>
                  <Button
                    loading={isSubmitting}
                    type="submit"
                    disabled={!isValid}
                  >
                    Tạo {rootId ? "Nhóm" : "Tổ chức"}
                  </Button>
                </DialogFooter>
              </Form>
            )}
          </Formik>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default memo(CreateChildGroup);
